import { useSetState } from '@mantine/hooks';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { isNil } from 'lodash-es';
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useApiGroupItemsRefreshSupport } from '@repo/api-codegen';
import { Title } from '@repo/foundations';
import type { IIntegration } from '../../../api';
import {
	fetchIntegrationById,
	integrationsQueryKeyFactory,
	useDeleteIntegration,
} from '../../../api';
import { useMarketplaceIntegrationSpec } from '../../../api/hooks/marketplace';
import type { IntegrationSpec } from '../../../interfaces/IntegrationSpec';
import { openConfirmModal } from '../../ModalManager';

interface CreateIntegrationState {
	createdIntegration?: IIntegration;
	currentStep: number;
}

export enum IntegrationSteps {
	CONNECTION = 'Connection',
	IMPORT = 'Import',
	MAPPING = 'Mapping',
	SYNC = 'Sync',
}

const useCreateIntegrationConnection = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const builtinIntegrationType = searchParams.get('type');
	const marketplaceIntegrationId = searchParams.get('selectedMarketplaceSpec');
	const integrationId = searchParams.get('integration_id');

	const navigate = useNavigate();

	const { mutateAsync: deleteIntegration } = useDeleteIntegration({
		invalidationKeys: [integrationsQueryKeyFactory.all()],
	});

	const builtinIntegrationSpec = integrationList.find(
		(integration) => integration.type === builtinIntegrationType
	);
	const { data: marketplaceIntegrationSpec } = useMarketplaceIntegrationSpec({
		id: marketplaceIntegrationId || '',
		options: {
			enabled: !isNil(marketplaceIntegrationId),
		},
	});

	const integrationSpec: IntegrationSpec = builtinIntegrationSpec
		? {
				type: 'builtin',
				value: builtinIntegrationSpec,
			}
		: {
				type: 'marketplace',
				value: marketplaceIntegrationSpec!,
			};

	const {
		data: isPreSelectGroupsSupported,
		isLoading: isRefreshSupportLoading,
	} = useApiGroupItemsRefreshSupport({
		queryParams: {
			integration_type: builtinIntegrationSpec?.type || 'marketplace',
		},
	});

	const [state, setState] = useSetState<CreateIntegrationState>({
		createdIntegration: undefined,
		currentStep: 0,
	});

	const prevStep = useCallback(
		() => setState((prevState) => ({ currentStep: prevState.currentStep - 1 })),
		[setState]
	);

	const nextStep = useCallback(
		() => setState((prevState) => ({ currentStep: prevState.currentStep + 1 })),
		[setState]
	);

	const setStep = useCallback(
		(step: number) => setState({ currentStep: step }),
		[setState]
	);

	useEffect(() => {
		if (integrationId && !state.createdIntegration) {
			fetchIntegrationById(integrationId).then((integration) => {
				setState({
					createdIntegration: integration,
				});
			});
		}
	}, [integrationId, state.createdIntegration]);

	const isMappingSupported =
		builtinIntegrationSpec &&
		(builtinIntegrationSpec.type === 'great_expectations' ||
			builtinIntegrationSpec.type === 'gx_core');

	// Check if this integration has disablePullMetadata set to true
	const hasDisablePullMetadata =
		builtinIntegrationSpec &&
		builtinIntegrationSpec.disablePullMetadata === true;

	const stepNames = [IntegrationSteps.CONNECTION];
	if (isPreSelectGroupsSupported) stepNames.push(IntegrationSteps.IMPORT);
	if (isMappingSupported) stepNames.push(IntegrationSteps.MAPPING);
	// Skip the sync step for integrations with disablePullMetadata
	if (!hasDisablePullMetadata) {
		stepNames.push(IntegrationSteps.SYNC);
	}

	const totalSteps = stepNames.length;
	const currentStepName = stepNames[state.currentStep];
	const isLastStep = state.currentStep === totalSteps - 1;

	const handleClose = useCallback(
		(onClose?: () => void) => {
			const confirmClose = () => {
				if (state.createdIntegration) {
					deleteIntegration({ id: state.createdIntegration.id });
				}
				onClose ? onClose() : navigate('/integrations');
			};

			if (!isLastStep) {
				openConfirmModal({
					title: <Title size="md">Leave page with unsaved changes?</Title>,
					children: 'All progress will be lost.',
					labels: { confirm: 'Leave page', cancel: 'Cancel' },
					cancelProps: { size: 'md' },
					confirmProps: { variant: 'primary', tone: 'default', size: 'md' },
					onConfirm: () => confirmClose(),
				});
			} else {
				onClose ? onClose() : navigate('/integrations');
			}
		},
		[deleteIntegration, isLastStep, navigate, state.createdIntegration]
	);

	return {
		integrationSpec,
		isPreSelectGroupsSupported,
		isRefreshSupportLoading,
		...state,
		setCreatedIntegration: (integration?: IIntegration) => {
			setState({ createdIntegration: integration });
			setSearchParams((prev) => {
				const params = new URLSearchParams(prev);
				if (integration) {
					params.set('integration_id', integration.id);
				} else {
					params.delete('integration_id');
				}
				return params;
			});
		},
		prevStep,
		nextStep,
		setStep,
		handleClose,
		stepNames,
		totalSteps,
		currentStepName,
		isLastStep,
	};
};

export default useCreateIntegrationConnection;
